<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { dndzone } from 'svelte-dnd-action';
  import {
    faRotate,
    faSquarePlus,
    faPencil,
    faTrash,
    faTrashCan,
    faClone,
    faCopy,
    faGripVertical,
    faHistory,
    faTimer,
    faMicrochip,
    faFileDoc,
    faFileMedical,
    faCalendar,
    faMicrochipAi,
    faFolders,
    faFile,
    faFiles,
  } from '@fortawesome/pro-light-svg-icons';
  import Toolbar from '$lib/Toolbar.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import ScheduleForm from './studySchedules/ScheduleForm.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import ActivityForm from './studySchedules/ActivityForm.svelte';
  import StudySelectModal from '$lib/StudySelectModal.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAuditLogAccess, hasAccessUiAction } from '$utils/permissionUtils.js';
  import FeatureInfo from '$lib/FeatureInfo.svelte';

  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';
  import { formatToLocalDate } from '$utils/dateUtils.js';

  let scheduleVersions = [];
  let selectedVersion = null;
  let activities = [];
  let resultCount = 0;
  let showForm = false;
  let editSchedule = null;
  let showConfirmation = false;
  let scheduleToDelete = null;
  let showActivityForm = false;
  let editActivity = null;
  let showActivityConfirmation = false;
  let activityToDelete = null;
  let showDuplicateConfirmation = false;
  let scheduleToDuplicate = null;
  let showStudySelectModal = false;
  let draggedActivity = null;
  let draggedIndex = -1;
  
  // Audit log state
  let showAuditLogSchedule = false;
  let auditLogSchedule = null;
  let showAuditLogActivity = false;
  let auditLogActivity = null;

  onMount(async () => {
    //selectedStudy.set(null);
    selectedSite.set(null);
    selectedParticipant.set(null);

    if ($selectedStudy && $selectedStudy._id) {
      fetchScheduleVersions();
    }
  });

  async function fetchScheduleVersions() {
    if (!$selectedStudy || !$selectedStudy._id) {
      console.warn('No study selected');
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/schedules/schedulesByStudy?studyGUID=${$selectedStudy._id}`
      );

      //console.log('res:', res);

      if (!res.ok) {
        throw new Error('Failed to fetch schedule versions');
      }

      const response = await res.json();
      scheduleVersions = Array.isArray(response.data) ? response.data : [];
      resultCount = response.totalCount || 0;
      //console.log('Schedule versions:', scheduleVersions);
    } catch (err) {
      console.error('Error fetching schedule versions:', err);
      scheduleVersions = [];
    }
  }

  async function fetchActivities(scheduleGUID) {
    try {
      const res = await fetch(
        `${API_BASE_URL}/activities/bySchedule?scheduleGUID=${scheduleGUID}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch activities');
      }

      const response = await res.json();
      
      // Map activities to include id property for dndzone
      activities = (Array.isArray(response.data) ? response.data : []).map(activity => ({
        ...activity,
        id: activity._id // Add id property for dndzone
      }));
    } catch (err) {
      console.error('Error fetching activities:', err);
      activities = [];
    }
  }

  function handleVersionClick(version) {
    selectedVersion = version;
    fetchActivities(version._id);
  }

  function handleRefresh() {
    fetchScheduleVersions();
    if (selectedVersion) {
      fetchActivities(selectedVersion._id);
    }
  }

  function secondsToDays(seconds) {
    if (!seconds) return '';
    return Math.round(seconds / (24 * 60 * 60));
  }
  
  // Add computed property for edit permissions
  $: canModifySchedules = !$userReadOnly;
  $: isAdmin = hasAccessUiAction(
    'studySchedules.activityForm.admin',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canViewAuditLogs = hasAuditLogAccess($userRoles, $studyProperties, $userReadOnly);
  
  async function handleCreateSchedule(event) {
    const newSchedule = event.detail;
    console.log('newSchedule:', newSchedule);
    try {
      const res = await fetch(`${API_BASE_URL}/schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSchedule),
      });

      console.log('res:', res);

      if (!res.ok) {
        throw new Error('Failed to create schedule');
      }

      await fetchScheduleVersions();
      showForm = false;
    } catch (err) {
      console.error('Error creating schedule:', err);
    }
  }

  async function handleEditSchedule(event) {
    const updatedSchedule = event.detail;
    console.log('updatedSchedule:', updatedSchedule);
    try {
      const res = await fetch(`${API_BASE_URL}/schedules/${updatedSchedule._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedSchedule),
      });

      if (!res.ok) {
        throw new Error('Failed to update schedule');
      }

      await fetchScheduleVersions();
      showForm = false;
      editSchedule = null;
    } catch (err) {
      console.error('Error updating schedule:', err);
    }
  }

  function handleNewClick() {
    editSchedule = null;
    showForm = true;
  }

  function handleDuplicateClick(schedule) {
    scheduleToDuplicate = schedule;
    showDuplicateConfirmation = true;
  }

  async function confirmDuplicate() {
    if (scheduleToDuplicate) {
      await handleDuplicateSchedule(scheduleToDuplicate);
      showDuplicateConfirmation = false;
      scheduleToDuplicate = null;
    }
  }

  async function handleDuplicateSchedule(schedule) {
    try {
      // Create a new schedule based on the published one
      const newSchedule = {
        studyGUID: schedule.studyGUID,
        name: `${schedule.name}`,
        description: schedule.description,
        active: true,
        status: 'Draft',
        protocolVersion: schedule.protocolVersion,
        scheduleVersion: schedule.scheduleVersion + 1,
        formGUID: schedule.formGUID
      };

      // Create the new schedule
      const scheduleRes = await fetch(`${API_BASE_URL}/schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSchedule),
      });

      if (!scheduleRes.ok) {
        throw new Error('Failed to duplicate schedule');
      }

      const createdSchedule = await scheduleRes.json();

      // Fetch all activities from the original schedule
      const activitiesRes = await fetch(
        `${API_BASE_URL}/activities/bySchedule?scheduleGUID=${schedule._id}`
      );

      if (!activitiesRes.ok) {
        throw new Error('Failed to fetch activities');
      }

      const activitiesData = await activitiesRes.json();
      const activities = activitiesData.data || [];

      // Create new activities for the new schedule
      for (const activity of activities) {
        const newActivity = {
          scheduleGUID: createdSchedule._id,
          name: activity.name,
          order: activity.order,
          code: activity.code,
          description: activity.description,
          activityId: activity.activityId,
          gap: activity.gap,
          windowMin: activity.windowMin,
          windowMax: activity.windowMax,
          readingCount: activity.readingCount,
          readingDuration: activity.readingDuration,
          readingFrequency: activity.readingFrequency,
          readingDurationDisplayUnitGUID: activity.readingDurationDisplayUnitGUID,
          readingFrequencyDisplayUnitGUID: activity.readingFrequencyDisplayUnitGUID,
          complianceThreshold: activity.complianceThreshold,
          active: activity.active,
          onDemand: activity.onDemand,
          allowDateEntryIndicator: activity.allowDateEntryIndicator,
          enableAlerts: activity.enableAlerts,
          complianceWindowMin: activity.complianceWindowMin,
          complianceWindowMax: activity.complianceWindowMax,
          cgmBlinded: activity.cgmBlinded,
          smbgBlinded: activity.smbgBlinded,
          activityTypeGUID: activity.activityTypeGUID,
          deviceGUID: activity.deviceGUID,
          formGUID: activity.formGUID,
          createDate: new Date(),
          lastUpdateDate: new Date(),
        };

        const activityRes = await fetch(`${API_BASE_URL}/activities`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newActivity),
        });

        if (!activityRes.ok) {
          throw new Error('Failed to duplicate activity');
        }
      }

      await fetchScheduleVersions();
    } catch (err) {
      console.error('Error duplicating schedule:', err);
    }
  }

  function handleCopyFromStudyClick() {
    showStudySelectModal = true;
  }

  async function handleStudySelected(event) {
    const selectedStudyId = event.detail;
    if (!selectedStudyId) {
      return;
    }

    try {
      // Get schedules from the selected study
      const res = await fetch(
        `${API_BASE_URL}/schedules/schedulesByStudy?studyGUID=${selectedStudyId}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch schedules from selected study');
      }

      const response = await res.json();
      const sourceSchedules = Array.isArray(response.data) ? response.data : [];
      
      if (sourceSchedules.length === 0) {
        alert('No schedules found in the selected study');
        return;
      }

      // Find the highest version schedule (published preferred)
      let highestVersionSchedule = sourceSchedules[0];
      
      for (const schedule of sourceSchedules) {
        if (schedule.scheduleVersion > highestVersionSchedule.scheduleVersion) {
          highestVersionSchedule = schedule;
        } else if (
          schedule.scheduleVersion === highestVersionSchedule.scheduleVersion && 
          schedule.status === 'Published' && 
          highestVersionSchedule.status !== 'Published'
        ) {
          // Prefer published versions if schedule versions are the same
          highestVersionSchedule = schedule;
        }
      }

      // Create a new schedule in the current study based on the highest version
      const newSchedule = {
        studyGUID: $selectedStudy._id,
        name: `${highestVersionSchedule.name}`,
        description: highestVersionSchedule.description,
        active: true,
        status: 'Draft',
        protocolVersion: highestVersionSchedule.protocolVersion,
        scheduleVersion: (scheduleVersions.length > 0 ? 
          Math.max(...scheduleVersions.map(v => v.scheduleVersion)) + 1 : 
          1),
        formGUID: highestVersionSchedule.formGUID
      };

      // Create the new schedule
      const scheduleRes = await fetch(`${API_BASE_URL}/schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSchedule),
      });

      if (!scheduleRes.ok) {
        throw new Error('Failed to create schedule in current study');
      }

      const createdSchedule = await scheduleRes.json();

      // Fetch all activities from the source schedule
      const activitiesRes = await fetch(
        `${API_BASE_URL}/activities/bySchedule?scheduleGUID=${highestVersionSchedule._id}`
      );

      if (!activitiesRes.ok) {
        throw new Error('Failed to fetch activities from source schedule');
      }

      const activitiesData = await activitiesRes.json();
      const activities = activitiesData.data || [];

      // Create new activities for the new schedule
      for (const activity of activities) {
        const newActivity = {
          scheduleGUID: createdSchedule._id,
          name: activity.name,
          order: activity.order,
          code: activity.code,
          description: activity.description,
          activityId: activity.activityId,
          gap: activity.gap,
          windowMin: activity.windowMin,
          windowMax: activity.windowMax,
          readingCount: activity.readingCount,
          readingDuration: activity.readingDuration,
          readingFrequency: activity.readingFrequency,
          readingDurationDisplayUnitGUID: activity.readingDurationDisplayUnitGUID,
          readingFrequencyDisplayUnitGUID: activity.readingFrequencyDisplayUnitGUID,
          complianceThreshold: activity.complianceThreshold,
          active: activity.active,
          onDemand: activity.onDemand,
          allowDateEntryIndicator: activity.allowDateEntryIndicator,
          enableAlerts: activity.enableAlerts,
          complianceWindowMin: activity.complianceWindowMin,
          complianceWindowMax: activity.complianceWindowMax,
          cgmBlinded: activity.cgmBlinded,
          smbgBlinded: activity.smbgBlinded,
          activityTypeGUID: activity.activityTypeGUID,
          deviceGUID: activity.deviceGUID,
          formGUID: activity.formGUID,
          createDate: new Date(),
          lastUpdateDate: new Date(),
        };

        const activityRes = await fetch(`${API_BASE_URL}/activities`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newActivity),
        });

        if (!activityRes.ok) {
          throw new Error('Failed to create activity');
        }
      }

      await fetchScheduleVersions();
      showStudySelectModal = false;
    } catch (err) {
      console.error('Error copying schedule from study:', err);
      alert(`Error copying schedule: ${err.message}`);
    }
  }

  function handleEditClick(schedule) {
    editSchedule = schedule;
    showForm = true;
  }

  function handleDeleteClick(schedule) {
    // console.log('Setting schedule to delete:', schedule);
    scheduleToDelete = { ...schedule }; // Create a copy to prevent reference issues
    showConfirmation = true;
  }

  async function confirmDelete() {
    if (!scheduleToDelete || !scheduleToDelete._id) {
      console.error('No schedule to delete or missing ID:', scheduleToDelete);
      alert('Error: Could not delete schedule - missing schedule ID');
      showConfirmation = false;
      return;
    }

    const scheduleId = scheduleToDelete._id; // Store ID locally to ensure it's available throughout
    
    try {
      // First fetch all activities for this schedule
      const activitiesRes = await fetch(
        `${API_BASE_URL}/activities/bySchedule?scheduleGUID=${scheduleId}`
      );

      if (!activitiesRes.ok) {
        throw new Error('Failed to fetch activities');
      }

      const activitiesData = await activitiesRes.json();
      const scheduleActivities = Array.isArray(activitiesData.data) ? activitiesData.data : [];
      
      // Delete all activities first
      for (const activity of scheduleActivities) {
        const deleteActivityRes = await fetch(`${API_BASE_URL}/activities/${activity._id}`, {
          method: 'DELETE'
        });

        if (!deleteActivityRes.ok) {
          throw new Error(`Failed to delete activity ${activity.name}`);
        }
      }

      // Then delete the schedule
      const scheduleDeleteUrl = `${API_BASE_URL}/schedules/${scheduleId}`;
      
      const res = await fetch(scheduleDeleteUrl, {
        method: 'DELETE'
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error(`Server response: ${errorText}`);
        throw new Error(`Failed to delete schedule: ${res.status} ${res.statusText}`);
      }

      await fetchScheduleVersions();
      
      // Clear the activities and selectedVersion if the deleted schedule was selected
      if (selectedVersion && selectedVersion._id === scheduleId) {
        activities = [];
        selectedVersion = null;
      }
      
      showConfirmation = false;
      scheduleToDelete = null;
    } catch (err) {
      console.error('Error deleting schedule:', err);
      alert(`Error deleting schedule: ${err.message}`);
    }
  }

  function handleNewActivity() {
    // console.log('Creating new activity');
    editActivity = null;
    showActivityForm = true;
  }

  function normalizeId(value) {
    if (value === undefined || value === null) return '';
    if (typeof value === 'object') {
      if (value.$oid && typeof value.$oid === 'string') return value.$oid;
      if (value._id) return normalizeId(value._id);
      if (typeof value.toString === 'function') {
        const s = value.toString();
        if (typeof s === 'string' && /^[a-fA-F0-9]{24}$/.test(s)) return s;
        return String(s || '');
      }
      return '';
    }
    return String(value);
  }

  function normalizeIdsArray(arr) {
    if (!Array.isArray(arr)) return [];
    return arr.map((v) => normalizeId(v)).filter((v) => v !== '');
  }

  async function handleEditActivity(activity) {
    // Ensure we have complete activity data from server (after navigation/state resets)
    let full = activity;
    try {
      const res = await fetch(`${API_BASE_URL}/activities/${activity._id}`);
      if (res.ok) {
        const data = await res.json();
        // Try common shapes
        const candidate =
          (data && (data.data || data.result || data.activity)) || data;
        // Only accept if it looks like an activity object
        if (candidate && (candidate._id || candidate.name || candidate.activityTypeGUID)) {
          full = candidate;
        } else {
          console.warn('Unexpected activity payload shape; using existing object.', data);
          full = activity;
        }
      }
    } catch (e) {
      console.warn('Failed to fetch full activity, using existing object.', e);
    }

    // Derive missing type code if necessary
    if (full.activityTypeGUID && !full.activityTypeCode) {
      const activityType = activities.find(a => a.activityTypeGUID === full.activityTypeGUID);
      if (activityType && activityType.activityTypeCode) {
        full.activityTypeCode = activityType.activityTypeCode;
      }
    }

    // Normalize group id arrays for UI binding
    full = {
      ...full,
      formGUIDs: normalizeIdsArray(full.formGUIDs),
      deviceGUIDs: normalizeIdsArray(full.deviceGUIDs)
    };

    editActivity = full;
    showActivityForm = true;
  }

  function handleDeleteActivity(activity) {
    activityToDelete = activity;
    showActivityConfirmation = true;
  }

  async function handleCreateActivity(event) {
    const newActivity = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newActivity),
      });

      if (!res.ok) {
        throw new Error('Failed to create activity');
      }

      await fetchActivities(selectedVersion._id);
      showActivityForm = false;
    } catch (err) {
      console.error('Error creating activity:', err);
    }
  }

  async function handleEditActivitySubmit(event) {
    const updatedActivity = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/activities/${updatedActivity._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedActivity),
      });

      if (!res.ok) {
        throw new Error('Failed to update activity');
      }

      await fetchActivities(selectedVersion._id);
      showActivityForm = false;
      editActivity = null;
    } catch (err) {
      console.error('Error updating activity:', err);
    }
  }

  async function confirmDeleteActivity() {
    if (activityToDelete) {
      try {
        const res = await fetch(`${API_BASE_URL}/activities/${activityToDelete._id}`, {
          method: 'DELETE'
        });

        if (!res.ok) {
          throw new Error('Failed to delete activity');
        }

        await fetchActivities(selectedVersion._id);
        showActivityConfirmation = false;
        activityToDelete = null;
      } catch (err) {
        console.error('Error deleting activity:', err);
      }
    }
  }

  function handleDndConsider(e) {
    // Map back to original format when updating activities
    activities = e.detail.items.map(item => ({
      ...item,
      _id: item.id // Ensure _id is preserved
    }));
  }

  async function handleDndFinalize(e) {
    // Map back to original format when finalizing and update order numbers
    activities = e.detail.items.map((item, index) => ({
      ...item,
      _id: item.id, // Ensure _id is preserved
      order: index + 1 // Update order number based on new position
    }));
    await updateActivityOrders();
  }

  async function updateActivityOrders() {
    if (!selectedVersion || selectedVersion.status !== 'Draft') return;

    try {
      // Update all activities with their new order numbers
      for (let i = 0; i < activities.length; i++) {
        const activity = activities[i];
        const updatedActivity = { ...activity, order: i + 1 };
        const res = await fetch(`${API_BASE_URL}/activities/${activity._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updatedActivity),
        });

        if (!res.ok) {
          throw new Error('Failed to update activity order');
        }
      }
    } catch (err) {
      console.error('Error updating activity orders:', err);
      // Refresh activities to revert to original order
      await fetchActivities(selectedVersion._id);
    }
  }

  function handleAuditLogScheduleClick(schedule) {
    auditLogSchedule = schedule;
    showAuditLogSchedule = true;
  }
  
  function handleAuditLogActivityClick(activity) {
    auditLogActivity = activity;
    showAuditLogActivity = true;
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="Study Schedules" 
  description="This page allows you to manage schedule templates for the study. You can create, edit, duplicate, and delete schedule versions. 
  Each schedule contains activities that define the phases (milestones) and devices participants will complete during the study. Use drag and drop to reorder activities. 
  Schedules can be copied from another study to the current study."
  marginBottom="mb-0"
/>

<StudyDetails showOptionsMenu={false} />

<Toolbar title="Schedules" itemCountTitle="Schedules" itemCount={resultCount}>
  <div class="flex items-center gap-2">
    <button 
      class="pagination mx-1 p-0" 
      on:click={handleNewClick}
      disabled={scheduleVersions.length > 0}
      class:opacity-50={scheduleVersions.length > 0}
      class:cursor-not-allowed={scheduleVersions.length > 0}
      title={scheduleVersions.length > 0 ? 'A schedule already exists' : 'Add Schedule'}
    >
      <FontAwesomeIcon icon={faSquarePlus} />
    </button>
    <button 
      class="pagination mx-1 p-0" 
      on:click={handleCopyFromStudyClick}
      disabled={!$selectedStudy || !$selectedStudy._id}
      class:opacity-50={!$selectedStudy || !$selectedStudy._id}
      class:cursor-not-allowed={!$selectedStudy || !$selectedStudy._id}
      title={!$selectedStudy || !$selectedStudy._id ? 'Select a study first' : 'Copy schedule from another study'}
    >
      <FontAwesomeIcon icon={faCopy} />
    </button>
    <button class="pagination mx-1 p-0" on:click={handleRefresh}>
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0 mb-5">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold">Name</th>
        <th class="px-4 py-2 text-left bold">Version</th>
        <th class="px-4 py-2 text-left bold">Created Date</th>
        <th class="px-4 py-2 text-left bold">Status</th>
        <th class="px-4 py-2 text-left bold">Active</th>
        <th class="px-4 py-2 text-left bold w-36">Actions</th>
      </tr>
    </thead>

    <tbody>
      {#each scheduleVersions as version, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2 cursor-pointer" on:click={() => handleVersionClick(version)}
            >{version.name}</td
          >
          <td class="px-4 py-2 cursor-pointer" on:click={() => handleVersionClick(version)}
            >{version.scheduleVersion}</td
          >
          <td class="px-4 py-2 cursor-pointer" on:click={() => handleVersionClick(version)}
            >{formatToLocalDate(version.createDate)}</td
          >
          <td class="px-4 py-2 cursor-pointer" on:click={() => handleVersionClick(version)}>
            <span
              class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
              class:text-green-700={version.status === 'Published'}
              class:ring-green-700={version.status === 'Published'}
              class:text-slate-600={version.status === 'Archived'}
              class:ring-slate-600={version.status === 'Archived'}
            >
              {version.status}
            </span>
          </td>
          <td class="px-4 py-2 cursor-pointer" on:click={() => handleVersionClick(version)}
            >{version.active}</td
          >
          <td class="w-px px-4 py-2">
            <button
              on:click={() => handleEditClick(version)}
              class="pagination mx-1 p-0"
              title="View Schedule Details"
            >
              <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
            </button>
                              <button
                    on:click={() => handleAuditLogScheduleClick(version)}
                    class="pagination mx-1 p-0"
                    title="View audit logs"
                    disabled={!canViewAuditLogs}
                    class:opacity-50={!canViewAuditLogs}
                  >
                    <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
                  </button>
            <button
              on:click={() => handleDeleteClick(version)}
              class="pagination mx-1 p-0"
              disabled={version.status === 'Published' || version.status === 'Archived'}
              class:opacity-50={version.status === 'Published' || version.status === 'Archived'}
              class:cursor-not-allowed={version.status === 'Published' || version.status === 'Archived'}
              title={version.status === 'Published' ? 'Published schedules cannot be deleted' :
                    version.status === 'Archived' ? 'Archived schedules cannot be deleted' :
                    'Delete Schedule'}
            >
              <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
            </button>
            <button
              on:click={() => handleDuplicateClick(version)}
              class="pagination mx-1 p-0"
              disabled={version.status !== 'Published'}
              class:opacity-50={version.status !== 'Published'}
              class:cursor-not-allowed={version.status !== 'Published'}
              title={version.status !== 'Published' ? 'Only Published schedules can be duplicated' : 
                    'Duplicate Schedule'}
            >
              <FontAwesomeIcon icon={faClone} class="text-sky-800 dark:text-white/70" />
            </button>
          </td>
        </tr>
      {/each}
      {#if scheduleVersions.length === 0}
        <tr>
          <td colspan="6" class="text-center text-gray-500 dark:text-slate-400 mt-10 py-10"
            >No schedule versions found</td
          >
        </tr>
      {/if}
    </tbody>
  </table>
</div>

<!-- Activities Section -->
{#if selectedVersion}
  <Toolbar
    title="Activities for Schedule Version {selectedVersion.scheduleVersion}"
    itemCountTitle="Activities"
    itemCount={activities.length}
  >
    <div class="flex items-center gap-2">
      <button
        class="pagination mx-1 p-0"
        on:click={handleNewActivity}
        disabled={selectedVersion.status !== 'Draft' && !isAdmin}
        class:opacity-50={selectedVersion.status !== 'Draft' && !isAdmin}
        class:cursor-not-allowed={selectedVersion.status !== 'Draft' && !isAdmin}
        title={selectedVersion.status !== 'Draft' && !isAdmin ? 'Activities can only be added in Draft mode' :
               !isAdmin ? 'Insufficient permissions to add activities' :
               'Add Activity'}
      >
        <FontAwesomeIcon icon={faSquarePlus} />
      </button>
    </div>
  </Toolbar>

  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold w-12"></th>
          <th class="px-4 py-2 text-left bold">Order</th>
          <th class="px-4 py-2 text-left bold">Type</th>
          <th class="px-4 py-2 text-left bold">Activity Name</th>
          <th class="px-4 py-2 text-left bold">Duration</th>
          <!-- <th class="px-4 py-2 text-left bold">Reading Duration</th>
          <th class="px-4 py-2 text-left bold">Reading Count</th> -->
          <th class="px-4 py-2 text-left bold">Alerts</th>
          <th class="px-4 py-2 text-left bold">On Demand</th>
          <th class="px-4 py-2 text-left bold">CGM Blinded</th>
          <th class="px-4 py-2 text-left bold">SMBG Blinded</th>
          <th class="px-4 py-2 text-left bold w-28">Actions</th>
        </tr>
      </thead>

      <tbody
        use:dndzone={{items: activities, flipDurationMs: 300, dragDisabled: selectedVersion.status !== 'Draft' || !canModifySchedules}}
        on:consider={handleDndConsider}
        on:finalize={handleDndFinalize}
      >
        {#each activities as activity, index (activity._id)}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
            data-id={activity._id}
          >
            <td class="px-4 py-2 w-12">
              {#if selectedVersion.status === 'Draft'}
                <div 
                  class="w-4 h-4 cursor-move flex items-center justify-center"
                  draggable="true"
                  title="Drag to reorder"
                >
                  <FontAwesomeIcon icon={faGripVertical} class="text-gray-400" />
                </div>
              {/if}
            </td>
            <td class="px-4 py-2">{activity.order}</td>
            <td class="px-4 py-2">
              {#if activity.activityTypeName === 'Milestone'}
                <FontAwesomeIcon icon={faTimer} class="text-sky-800 dark:text-white/70" title="Milestone" />
              {:else if activity.activityTypeName === 'Device'}
                <FontAwesomeIcon icon={faMicrochip} class="text-sky-800 dark:text-white/70" title="Device" />
              {:else if activity.activityTypeName === 'Form'}
                <FontAwesomeIcon icon={faFile} class="text-sky-800 dark:text-white/70" title="Diary" />
              {:else if activity.activityTypeName === 'Device Group'}
                <FontAwesomeIcon icon={faMicrochipAi} class="text-sky-800 dark:text-white/70" title="Device Group" />
              {:else if activity.activityTypeName === 'Form Group'}
                <FontAwesomeIcon icon={faFiles} class="text-sky-800 dark:text-white/70" title="Diary Group" />
              {/if}
            </td>
            <td class="px-4 py-2">{activity.name}</td>
            <td class="px-4 py-2"
              >{activity.gap > 0 ? `${secondsToDays(activity.gap)} days` : ''}</td
            >
            <!-- <td class="px-4 py-2"
              >{activity.readingDuration > 0 ? `${secondsToDays(activity.readingDuration)} days` : ''}</td
            >
            <td class="px-4 py-2"
              >{activity.readingCount > 0 ? activity.readingCount : ''}</td
            > -->
            <td class="px-4 py-2">{activity.enableAlerts ? 'true' : ''}</td>
            <td class="px-4 py-2">{activity.onDemand ? 'true' : ''}</td>
            <td class="px-4 py-2">{activity.cgmBlinded ? 'true' : ''}</td>
            <td class="px-4 py-2">{activity.smbgBlinded ? 'true' : ''}</td>
            <td class="w-px px-4 py-2">
              <button
                on:click={() => handleEditActivity(activity)}
                class="pagination mx-1 p-0"
                title="View Activity Details"
              >
                <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
              </button>
                                <button
                    on:click={() => handleAuditLogActivityClick(activity)}
                    class="pagination mx-1 p-0"
                    title="View audit logs"
                    disabled={!canViewAuditLogs}
                    class:opacity-50={!canViewAuditLogs}
                  >
                    <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
                  </button>
              <button
                on:click={() => handleDeleteActivity(activity)}
                class="pagination mx-1 p-0"
                disabled={selectedVersion.status !== 'Draft' && !isAdmin}
                class:opacity-50={selectedVersion.status !== 'Draft' && !isAdmin}
                class:cursor-not-allowed={selectedVersion.status !== 'Draft' && !isAdmin}
                title={selectedVersion.status !== 'Draft' && !isAdmin ? 'Activities can only be deleted in Draft mode' :
                       !isAdmin ? 'Insufficient permissions to delete activities' :
                       'Delete Activity'}
              >
                <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
              </button>
            </td>
          </tr>
        {/each}
        {#if activities.length === 0}
          <tr>
            <td colspan="11" class="text-center text-gray-500 dark:text-slate-400 mt-10 py-10"
              >No activities found</td
            >
          </tr>
        {/if}
      </tbody>
    </table>
  </div>
{/if}

<ConfirmationModal
  bind:showConfirmation
  message={`Are you sure you want to delete schedule "${scheduleToDelete?.name}"? This action cannot be undone.`}
  on:close={() => {
    showConfirmation = false;
    scheduleToDelete = null;
  }}
  on:confirm={confirmDelete}
/>

<ScheduleForm
  {showForm}
  {editSchedule}
  scheduleStatus={editSchedule?.status}
  on:close={() => {
    showForm = false;
    editSchedule = null;
  }}
  on:submit={editSchedule ? handleEditSchedule : handleCreateSchedule}
/>

<ActivityForm
  bind:showForm={showActivityForm}
  {editActivity}
  scheduleGUID={selectedVersion?._id}
  scheduleStatus={selectedVersion?.status}
  on:close={() => {
    showActivityForm = false;
    editActivity = null;
  }}
  on:submit={editActivity ? handleEditActivitySubmit : handleCreateActivity}
/>

<ConfirmationModal
  bind:showConfirmation={showActivityConfirmation}
  message={`Are you sure you want to delete activity "${activityToDelete?.name}"? This action cannot be undone.`}
  on:close={() => {
    showActivityConfirmation = false;
    activityToDelete = null;
  }}
  on:confirm={confirmDeleteActivity}
/>

<ConfirmationModal
  bind:showConfirmation={showDuplicateConfirmation}
  message={`Are you sure you want to duplicate schedule "${scheduleToDuplicate?.name}"?`}
  confirmPhrase="duplicate"
  on:close={() => {
    showDuplicateConfirmation = false;
    scheduleToDuplicate = null;
  }}
  on:confirm={confirmDuplicate}
/>

<StudySelectModal
  bind:showModal={showStudySelectModal}
  currentStudyId={$selectedStudy?._id}
  on:studySelected={handleStudySelected}
  on:close={() => {
    showStudySelectModal = false;
  }}
/>

<!-- Schedule Audit Log Modal -->
{#if showAuditLogSchedule}
  <AuditLogModal
    showModal={showAuditLogSchedule}
    documentId={auditLogSchedule?._id}
    collection="Schedule"
    title={`Audit Logs - Schedule "${auditLogSchedule?.name || ''}" (v${auditLogSchedule?.scheduleVersion || ''})`}
    on:close={() => (showAuditLogSchedule = false)}
  />
{/if}

<!-- Activity Audit Log Modal -->
{#if showAuditLogActivity}
  <AuditLogModal
    showModal={showAuditLogActivity}
    documentId={auditLogActivity?._id}
    collection="Activity"
    title={`Audit Logs - Activity "${auditLogActivity?.name || ''}"`}
    on:close={() => (showAuditLogActivity = false)}
  />
{/if}
